import { db, getCachedProfileByHandle, getManifest, kvGet, kvSet, kvDelete, logFailure } from '../cache/db'
import { gunzipJson } from '../cache/gzip'
import { hydratePostUris } from '../api/hydratePosts'
import { COLLECTIONS, pullCollection, type CollectionKey } from '../api/pull'
import { reencode } from '../cache/compactor'
import { allImagesOf, isAdultPost } from '../lib/embedMedia'
import { useTabs } from '../store/tabs'
import { useAccounts } from '../store/accounts'
import { useArchive } from '../store/archive'
import { RAW_CACHE } from '../variant'
import type { PostEmbedView } from '../components/PostMedia'

/**
 * Disk archive (Chrome/Edge File System Access API).
 *
 * Mirrors each watched user's cached content into a real folder OUTSIDE the
 * browser's origin storage — human-browsable, quota-free, and it survives a
 * full browser-profile wipe:
 *
 *   <folder>/
 *     index.yaml
 *     <handle>/
 *       posts/<year>/<yyyy-mm-dd>--<rkey>.yaml    (own posts/replies ONLY —
 *                                                  likes/reposts get no YAML)
 *       media/posts/<rkey>-<n>.webp   media/posts/adult/…
 *       media/reposts/…  media/reposts/adult/…
 *       media/likes/…    media/likes/adult/…
 *     (walk order per user: posts → reposts → likes)
 *
 * YAML chosen over JSON for readability (post text renders as a `|` block).
 * Media bytes come from the readable image cache (post-compaction) or the
 * worker's /api/img proxy, then re-encode through the compactor's encoder.
 * Videos: the full HLS stream (highest variant) is downloaded and saved as
 * a playable MPEG-TS file next to the poster (H.264 as served — see
 * downloadVideo for why there's no in-browser H.265 pass).
 *
 * The disk copy is the PRIMARY store (v1.37.0): every archived media URL is
 * mapped (META_CACHE) so the service worker can render tiles straight from
 * the folder, and verified-on-disk cache entries are EVICTED from Cache
 * Storage after each run — eviction never happens without the file's bytes
 * being confirmed on disk first.
 *
 * WHAT GETS ARCHIVED (owner's rules, v1.34.0):
 *  - the user's OWN posts/replies: always — full text + date (+ any media).
 *  - their likes + reposts: ONLY when the subject post has media (images
 *    or a video poster) — the point is preserving media. Text-only likes/
 *    reposts are skipped entirely; never write bare-URI records.
 *
 * Incremental: a per-user fetched_at cursor in the kv table; re-runs only
 * touch new rows, and rewrites are idempotent (same path, same content).
 */

const DIR_KEY = 'archive:dir'
const cursorKey = (handle: string, source: string) => `archive:cursor:${source}:${handle}`
const countKey = (handle: string) => `archive:count:${handle}`

const IMAGE_CACHE = 'bskyhq-images-v1'
const META_CACHE = 'bskyhq-compact-meta-v1'
/** CDN-url → archive-file map entries live in META_CACHE under this
 *  synthetic prefix. The service worker reads the same map to serve
 *  images straight from the folder (and to skip prefetching them). */
const MAP_PREFIX = 'https://amap.bskyhq.invalid/?u='
const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '')

async function putMapEntries(urls: (string | undefined)[], fullPath: string): Promise<void> {
  try {
    const map = await caches.open(META_CACHE)
    for (const u of urls) {
      if (!u) continue
      await map
        .put(
          MAP_PREFIX + encodeURIComponent(u),
          new Response(JSON.stringify({ p: fullPath }), {
            headers: { 'content-type': 'application/json' },
          }),
        )
        .catch(() => {})
    }
  } catch {
    /* map is an optimization — archiving itself already succeeded */
  }
}

const BOOT_DELAY_MS = 30_000
/** Fallback cadence ONLY — the primary trigger is the gallery-refreshed
 *  event, which fires the moment a poll lands fresh content. */
const PERIODIC_MS = 15 * 60_000
/** Debounce after a gallery refresh before archiving (let hydration +
 *  image warming settle), and minimum spacing between event-driven runs. */
const REFRESH_DEBOUNCE_MS = 10_000
const MIN_RUN_SPACING_MS = 2 * 60_000
const PACE_MS = 50
/** Max record URIs hydrated from AppView PER USER PER RUN. Raw pulled
 *  records (db.records) need AppView hydration (image URLs, counts) before
 *  they can be archived; this caps one run's API load while self-chaining
 *  (see runArchiver) keeps re-running every 30s until the backlog is zero
 *  — so full histories (2024, 2023, …) drain automatically. */
const HYDRATE_CAP_PER_USER = 5_000
const HYDRATE_BATCH = 500

// ─────────── connection / permission ───────────

export function archiveSupported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window
}

export async function connectArchiveFolder(): Promise<string | null> {
  if (!archiveSupported()) return null
  const dir = await window.showDirectoryPicker({ id: 'bskyhq-archive', mode: 'readwrite' })
  await kvSet(DIR_KEY, dir)
  useArchive.getState().set({ ready: true, folderName: dir.name, needsPermission: false, error: null })
  return dir.name
}

export async function disconnectArchiveFolder(): Promise<void> {
  await kvDelete(DIR_KEY)
  useArchive.getState().set({ ready: false, folderName: null, needsPermission: false })
}

let permFailureLogged = false

async function getDir(requestPermission: boolean): Promise<FileSystemDirectoryHandle | null> {
  const dir = await kvGet<FileSystemDirectoryHandle>(DIR_KEY)
  if (!dir) {
    useArchive.getState().set({ ready: false, folderName: null, needsPermission: false })
    return null
  }
  try {
    let perm = await dir.queryPermission({ mode: 'readwrite' })
    if (perm === 'prompt' && requestPermission) {
      // Needs a user gesture — only the Settings button passes true.
      perm = await dir.requestPermission({ mode: 'readwrite' })
    }
    const ok = perm === 'granted'
    useArchive.getState().set({
      ready: ok,
      folderName: dir.name,
      needsPermission: !ok && perm === 'prompt',
    })
    if (!ok && !permFailureLogged) {
      permFailureLogged = true
      void logFailure({
        op: 'archive',
        target: `disk archive folder "${dir.name}"`,
        reason:
          'archive folder handle lost its readwrite permission — background archiving is paused; re-grant from Settings → disk archive',
      })
    }
    return ok ? dir : null
  } catch {
    useArchive.getState().set({ ready: false, folderName: dir.name, needsPermission: true })
    if (!permFailureLogged) {
      permFailureLogged = true
      void logFailure({
        op: 'archive',
        target: 'disk archive folder',
        reason: 'archive folder handle is unreadable (moved/deleted?) — reconnect from Settings → disk archive',
      })
    }
    return null
  }
}

/** One-click re-grant from Settings (user gesture context). */
export async function regrantArchivePermission(): Promise<boolean> {
  return (await getDir(true)) != null
}

// ─────────── YAML (purpose-built emitter for our record shape) ───────────

function yScalar(v: string): string {
  // Quote anything YAML could misread; plain for the common at://… / dates.
  if (v === '' || /[:#\[\]{}&*!|>'"%@`,\n]|^[\s-?]|\s$/.test(v)) {
    return `"${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`
  }
  return v
}

function yBlock(text: string, indent: string): string {
  // Block scalar keeps post text human-readable.
  const body = text
    .split('\n')
    .map((l) => `${indent}  ${l}`)
    .join('\n')
  return `|-\n${body}`
}

interface ArchiveRecord {
  uri: string
  author: string
  source: 'post' | 'reply' | 'repost' | 'like'
  date: string
  adult: boolean
  video?: boolean
  text?: string
  media?: string[]
  counts?: { likes?: number; reposts?: number; replies?: number }
}

function toYaml(r: ArchiveRecord): string {
  const lines: string[] = []
  lines.push(`uri: ${yScalar(r.uri)}`)
  lines.push(`author: ${yScalar(r.author)}`)
  lines.push(`source: ${r.source}`)
  lines.push(`date: ${yScalar(r.date)}`)
  lines.push(`adult: ${r.adult}`)
  if (r.video) lines.push('video: true')
  if (r.text != null && r.text !== '') lines.push(`text: ${yBlock(r.text, '')}`)
  if (r.media && r.media.length > 0) {
    lines.push('media:')
    for (const m of r.media) lines.push(`  - ${yScalar(m)}`)
  }
  if (r.counts) {
    lines.push(
      `counts: { likes: ${r.counts.likes ?? 0}, reposts: ${r.counts.reposts ?? 0}, replies: ${r.counts.replies ?? 0} }`,
    )
  }
  return lines.join('\n') + '\n'
}

// ─────────── fs helpers ───────────

async function writeFile(
  dir: FileSystemDirectoryHandle,
  path: string[],
  data: Blob | string,
): Promise<void> {
  let d = dir
  for (const part of path.slice(0, -1)) {
    d = await d.getDirectoryHandle(part, { create: true })
  }
  const fh = await d.getFileHandle(path[path.length - 1], { create: true })
  const w = await fh.createWritable()
  await w.write(data)
  await w.close()
}

/** Disk-first: if this media item was already archived (any encoding),
 *  return its relative path so the fetch + re-encode is skipped entirely.
 *  Zero-byte files (interrupted writes) don't count. */
async function existingMedia(
  dir: FileSystemDirectoryHandle,
  userDir: string,
  folder: string, // may be nested, e.g. 'likes/adult'
  base: string,
): Promise<string | null> {
  let d = dir
  try {
    d = await d.getDirectoryHandle(userDir)
    d = await d.getDirectoryHandle('media')
    for (const part of folder.split('/')) d = await d.getDirectoryHandle(part)
  } catch {
    return null
  }
  for (const ext of ['webp', 'jpg', 'png']) {
    try {
      const fh = await d.getFileHandle(`${base}.${ext}`)
      const f = await fh.getFile()
      if (f.size > 0) return `media/${folder}/${base}.${ext}`
    } catch {
      /* try next extension */
    }
  }
  return null
}

// ─────────── content gathering ───────────

interface PostLike {
  uri: string
  cid?: string
  record?: { text?: string; reply?: unknown; createdAt?: string }
  embed?: Record<string, unknown>
  indexedAt?: string
  likeCount?: number
  repostCount?: number
  replyCount?: number
  author?: { did?: string; handle?: string }
}

async function inflate<T>(row: { z?: Uint8Array; value?: unknown }): Promise<T | null> {
  try {
    if (row.z) return await gunzipJson<T>(row.z)
    return (row.value as T) ?? null
  } catch {
    return null
  }
}

async function mediaBlob(url: string): Promise<Blob | null> {
  try {
    const c = await caches.open(IMAGE_CACHE)
    const r = await c.match(url)
    if (r && r.type !== 'opaque') return await r.blob()
  } catch {
    /* fall through to proxy */
  }
  if (!API_BASE) return null
  try {
    const r = await fetch(`${API_BASE}/api/img?u=${encodeURIComponent(url)}`, { mode: 'cors' })
    if (r.ok) return await r.blob()
  } catch {
    /* offline / gone upstream */
  }
  return null
}

function rkeyOf(uri: string): string {
  return uri.split('/').pop() ?? uri.replace(/[^a-z0-9]/gi, '_')
}

/** Pull the HLS playlist URL out of a video embed view (direct or nested
 *  under media for record-with-media embeds). */
function playlistOf(embed: unknown): string | null {
  const e = embed as { playlist?: unknown; media?: { playlist?: unknown } } | undefined
  if (!e) return null
  if (typeof e.playlist === 'string') return e.playlist
  if (typeof e.media?.playlist === 'string') return e.media.playlist
  return null
}

/** Download a whole HLS video (highest-bandwidth variant) as one playable
 *  MPEG-TS blob: init segment (if any) + every media segment, in order.
 *  video.bsky.app serves CORS, so this needs no proxy. The stream stays
 *  H.264 as served — in-browser H.265 transcode isn't dependable (HEVC
 *  encode is hardware-gated in WebCodecs); re-encode on disk with
 *  `ffmpeg -i in.ts -c:v libx265 -crf 26 -c:a copy out.mp4` if wanted. */
async function downloadVideo(playlistUrl: string): Promise<Blob | null> {
  try {
    const masterResp = await fetch(playlistUrl)
    if (!masterResp.ok) return null
    const master = await masterResp.text()
    const masterBase = new URL(playlistUrl)
    let best: string | null = null
    let bestBw = -1
    const lines = master.split('\n')
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].startsWith('#EXT-X-STREAM-INF')) {
        const bw = Number(/BANDWIDTH=(\d+)/.exec(lines[i])?.[1] ?? 0)
        const uri = lines[i + 1]?.trim()
        if (uri && !uri.startsWith('#') && bw > bestBw) {
          bestBw = bw
          best = uri
        }
      }
    }
    // No variants → the master IS the media playlist.
    const variantUrl = best ? new URL(best, masterBase).toString() : playlistUrl
    const mediaResp = best ? await fetch(variantUrl) : masterResp
    const media = best ? (mediaResp.ok ? await mediaResp.text() : null) : master
    if (media == null) return null
    const vbase = new URL(variantUrl)
    const segUrls: string[] = []
    for (const raw of media.split('\n')) {
      const l = raw.trim()
      if (l.startsWith('#EXT-X-MAP')) {
        const uri = /URI="([^"]+)"/.exec(l)?.[1]
        if (uri) segUrls.push(new URL(uri, vbase).toString())
      } else if (l && !l.startsWith('#')) {
        segUrls.push(new URL(l, vbase).toString())
      }
    }
    if (segUrls.length === 0) return null
    const parts: ArrayBuffer[] = []
    for (const s of segUrls) {
      const r = await fetch(s)
      if (!r.ok) return null // partial video = corrupt file; retry next run
      parts.push(await r.arrayBuffer())
      await new Promise((res) => setTimeout(res, 30))
    }
    return new Blob(parts, { type: 'video/mp2t' })
  } catch {
    return null
  }
}

/** True when the file at parts exists with real bytes. */
async function fileHasBytes(dir: FileSystemDirectoryHandle, parts: string[]): Promise<boolean> {
  try {
    let d = dir
    for (const p of parts.slice(0, -1)) d = await d.getDirectoryHandle(p)
    const fh = await d.getFileHandle(parts[parts.length - 1])
    return (await fh.getFile()).size > 0
  } catch {
    return false
  }
}

function dateOf(p: PostLike): string {
  return p.record?.createdAt ?? p.indexedAt ?? new Date(0).toISOString()
}

/** Archive one post-shaped item: media files first, then the YAML record.
 *  With `mediaOnly`, text-only posts are skipped outright (likes/reposts
 *  archive for their media, not their text). */
async function archivePost(
  dir: FileSystemDirectoryHandle,
  userDir: string,
  source: ArchiveRecord['source'],
  p: PostLike,
  mediaOnly = false,
): Promise<boolean> {
  try {
    const rkey = rkeyOf(p.uri)
    const date = dateOf(p)
    const day = date.slice(0, 10)
    const year = date.slice(0, 4) || '0000'
    const embed = p.embed as PostEmbedView | undefined
    const images = embed ? allImagesOf(embed, p.author?.did) : []
    if (mediaOnly && images.length === 0) return false
    const isVideo = !!embed && JSON.stringify(embed).includes('video')
    const adult = isAdultPost(p as Parameters<typeof isAdultPost>[0])
    // Media sorts into per-source subfolders alongside the YAML tree —
    // media/posts/, media/likes/, media/reposts/ — with adult content in
    // its own adult/ subfolder inside each.
    const folder = source === 'like' ? 'likes' : source === 'repost' ? 'reposts' : 'posts'
    const sub = adult ? `${folder}/adult` : folder
    const mediaPaths: string[] = []
    let i = 0
    for (const m of images) {
      i++
      // Disk first: never re-download what's already in the folder. For
      // adult posts, files archived before the adult/ split (flat location)
      // still count — they stay where they are, never re-fetched.
      const already =
        (await existingMedia(dir, userDir, sub, `${rkey}-${i}`)) ??
        (adult ? await existingMedia(dir, userDir, folder, `${rkey}-${i}`) : null)
      const url = m.fullsize ?? m.src
      if (!url) continue
      if (already) {
        mediaPaths.push(already)
        await putMapEntries([m.src, m.fullsize], `${userDir}/${already}`)
        continue
      }
      const raw = await mediaBlob(url)
      if (!raw) continue
      const small = await reencode(raw, 2048, 0.82)
      const out = small && small.size < raw.size * 0.95 ? small : raw
      const ext = out.type.includes('webp') ? 'webp' : out.type.includes('png') ? 'png' : 'jpg'
      const name = `${rkey}-${i}.${ext}`
      await writeFile(dir, [userDir, 'media', ...sub.split('/'), name], out)
      mediaPaths.push(`media/${sub}/${name}`)
      // Map both thumb + fullsize URLs so the SW can serve either from disk.
      await putMapEntries([m.src, m.fullsize], `${userDir}/media/${sub}/${name}`)
    }
    // Video: pull the whole HLS stream to disk as a playable .ts file.
    const playlist = isVideo ? playlistOf(embed) : null
    if (playlist) {
      const vName = `${rkey}.ts`
      if (await fileHasBytes(dir, [userDir, 'media', ...sub.split('/'), vName])) {
        mediaPaths.push(`media/${sub}/${vName}`)
      } else if (adult && (await fileHasBytes(dir, [userDir, 'media', folder, vName]))) {
        mediaPaths.push(`media/${folder}/${vName}`) // pre-split location
      } else {
        const v = await downloadVideo(playlist)
        if (v && v.size > 0) {
          await writeFile(dir, [userDir, 'media', ...sub.split('/'), vName], v)
          mediaPaths.push(`media/${sub}/${vName}`)
        }
      }
    }
    // Likes/reposts are MEDIA-ONLY: the files above are the archive — no
    // YAML record is written for them (owner's rule). Success = got media.
    if (mediaOnly) return mediaPaths.length > 0
    const rec: ArchiveRecord = {
      uri: p.uri,
      author: p.author?.handle ?? userDir,
      source,
      date,
      adult,
      video: isVideo || undefined,
      text: p.record?.text,
      media: mediaPaths.length > 0 ? mediaPaths : undefined,
      counts: {
        likes: p.likeCount ?? 0,
        reposts: p.repostCount ?? 0,
        replies: p.replyCount ?? 0,
      },
    }
    await writeFile(dir, [userDir, folder, year, `${day}--${rkey}.yaml`], toYaml(rec))
    return true
  } catch {
    return false
  }
}

/** Resolve the user's DID when the tab lacks one. TRUSTED sources only:
 *  the tab itself or the cached profile row. Never guess from record URIs
 *  — the v1.40.0 repo-fallback bug filed foreign records under this
 *  handle, so a record's URI can lie about whose repo it came from. */
async function resolveDid(handle: string, current: string | null): Promise<string | null> {
  if (current) return current
  try {
    const prof = await getCachedProfileByHandle(handle)
    if (prof?.did) return prof.did
  } catch {
    /* unresolved — pulls + posts walk skipped this run, retried next */
  }
  return null
}

/** Collect at:// URIs referenced by this user's cached records that are
 *  NOT yet in db.posts: their own pulled posts plus like/repost subjects.
 *  These exist as raw PDS records (no image URLs), so without hydration
 *  the archiver would silently skip them forever. */
async function missingUrisFor(handle: string): Promise<string[]> {
  const want = new Set<string>()
  // Own posts: pulled records whose URI isn't hydrated yet.
  const postRows = await db.records
    .where('[handle+collection+rkey]')
    .between([handle, 'app.bsky.feed.post', ''], [handle, 'app.bsky.feed.post', '￿'])
    .toArray()
  for (const r of postRows) if (r.uri) want.add(r.uri)
  // Like/repost subjects.
  for (const collection of ['app.bsky.feed.like', 'app.bsky.feed.repost']) {
    const rows = await db.records
      .where('[handle+collection+rkey]')
      .between([handle, collection, ''], [handle, collection, '￿'])
      .toArray()
    for (const r of rows) {
      const val = await inflate<{ subject?: { uri?: string } }>(r)
      if (val?.subject?.uri) want.add(val.subject.uri)
    }
  }
  if (want.size === 0) return []
  const uris = [...want]
  const hydrated = await db.posts.bulkGet(uris)
  return uris.filter((_, i) => !hydrated[i])
}

/** Delete image-cache entries whose archived file verifies on disk.
 *  STRICT: no verification → no eviction, ever. Returns entries dropped. */
async function evictArchived(dir: FileSystemDirectoryHandle): Promise<number> {
  let evicted = 0
  try {
    const img = await caches.open(IMAGE_CACHE)
    const map = await caches.open(META_CACHE)
    for (const req of await img.keys()) {
      try {
        const hit = await map.match(MAP_PREFIX + encodeURIComponent(req.url))
        if (!hit) continue
        const { p } = (await hit.json()) as { p?: string }
        if (!p) continue
        if (await fileHasBytes(dir, p.split('/'))) {
          if (await img.delete(req)) evicted++
        }
      } catch {
        /* keep the cache copy on any doubt */
      }
      await new Promise((r) => setTimeout(r, 5))
    }
  } catch {
    /* cache enumeration failed — nothing evicted */
  }
  return evicted
}

/** One-time decontamination for the v1.40.0 repo-fallback bug: MY OWN
 *  records were pulled and filed under other users' handles (showing my
 *  posts on their profiles). Deletes every db.records row whose at:// URI
 *  belongs to one of my signed-in accounts but sits under a different
 *  handle; resets pull meta/manifests for the touched pairs so clean full
 *  pulls re-run; clears tab DIDs that were poisoned by those rows. */
export async function decontaminateRecords(): Promise<void> {
  if (await kvGet<boolean>('archive:decontam-v1')) return
  try {
    const accounts = useAccounts.getState().accounts
    if (accounts.length === 0) return // can't identify my repos yet — retry next boot
    const mine = new Map<string, string>()
    for (const a of accounts) {
      if (a.did && a.handle) mine.set(a.did, a.handle.toLowerCase())
    }
    const badKeys: [string, string, string][] = []
    const touched = new Set<string>()
    await db.records.each((row) => {
      const m = row.uri?.match(/^at:\/\/([^/]+)\//)
      if (!m) return
      const ownerHandle = mine.get(m[1])
      if (ownerHandle && row.handle !== ownerHandle) {
        badKeys.push([row.handle, row.collection, row.rkey])
        touched.add(`${row.handle}|${row.collection}`)
      }
    })
    if (badKeys.length > 0) {
      await db.records.bulkDelete(badKeys)
      // Reset sync bookkeeping under BOTH id styles (meta uses the short
      // key, manifest the nsid) so correct full pulls re-run from scratch.
      const KEY_BY_NSID: Record<string, string> = {
        'app.bsky.feed.post': 'posts',
        'app.bsky.feed.repost': 'reposts',
        'app.bsky.feed.like': 'likes',
      }
      for (const pair of touched) {
        const [h, nsid] = pair.split('|')
        await db.pull_manifest.delete(`${h}|${nsid}`).catch(() => {})
        await db.pull_meta.delete(`${h}|${nsid}`).catch(() => {})
        const short = KEY_BY_NSID[nsid]
        if (short) {
          await db.pull_manifest.delete(`${h}|${short}`).catch(() => {})
          await db.pull_meta.delete(`${h}|${short}`).catch(() => {})
        }
      }
      // Tabs whose did was guessed from poisoned rows point at MY repo —
      // null them so resolveDid re-derives from the cached profile.
      const tabsStore = useTabs.getState()
      for (const t of tabsStore.tabs) {
        if (t.did && mine.has(t.did) && t.handle !== mine.get(t.did)) {
          tabsStore.patch(t.handle, { did: null })
        }
      }
      void logFailure({
        op: 'archive',
        target: 'decontamination',
        reason: `removed ${badKeys.length} record(s) mis-filed under other users by the v1.40.0 repo-fallback pull bug (fixed in v1.42.1); affected collections will re-pull cleanly`,
      })
      // eslint-disable-next-line no-console
      console.info(`[bskyhq] decontamination: removed ${badKeys.length} mis-filed record(s)`)
    }
    await kvSet('archive:decontam-v1', true)
  } catch {
    /* flag not set — retries next boot */
  }
}

// ─────────── the run loop ───────────

let runningLocal = false

export async function runArchiver(opts: { requestPermission?: boolean } = {}): Promise<void> {
  if (RAW_CACHE) return // raw variant: no disk archive at all
  if (runningLocal) return
  if (!archiveSupported()) return
  const dir = await getDir(opts.requestPermission ?? false)
  if (!dir) return

  const run = async () => {
    runningLocal = true
    const store = useArchive.getState()
    store.set({ running: true, phase: 'starting', error: null })
    let wroteTotal = 0
    let backlogRemaining = 0
    try {
      const tabs = useTabs.getState().tabs
      for (const tab of tabs) {
        const handle = tab.handle
        let wrote = 0
        const did = await resolveDid(handle, tab.did)
        if (did && !tab.did) useTabs.getState().patch(handle, { did })

        // ── full-history guarantee: the gallery window only shows a slice,
        // and polling only accumulates recent items — but the archive's job
        // is EVERYTHING AVAILABLE. Walk every not-yet-completed collection
        // to cursor-end, in the owner's order: posts → reposts → likes.
        // Pulls REQUIRE the trusted DID — without one we skip rather than
        // let any fallback touch the wrong repo.
        if (did) {
          for (const key of ['posts', 'reposts', 'likes'] as CollectionKey[]) {
            try {
              const m = await getManifest(handle, COLLECTIONS[key])
              if (m?.completed) continue
              store.set({ phase: `@${handle} full ${key} pull (history)` })
              await pullCollection(key, { targetHandle: handle, targetDid: did, full: true })
            } catch {
              break /* pull failed (offline / no account) — retry next run */
            }
          }
        }

        // ── hydrate the pull gap: raw records (from the full pulls above /
        // profile "pull all") whose AppView views aren't cached yet.
        // Hydrated rows get a fresh fetched_at, so the walks below pick
        // them up this same run. Per-user budget; leftovers counted into
        // backlogRemaining, which makes the run self-chain until empty.
        try {
          store.set({ phase: `@${handle} scanning for unarchived history` })
          const missing = await missingUrisFor(handle)
          const toDo = missing.slice(0, HYDRATE_CAP_PER_USER)
          for (let off = 0; off < toDo.length; off += HYDRATE_BATCH) {
            store.set({
              phase: `@${handle} hydrating history ${Math.min(off + HYDRATE_BATCH, toDo.length)}/${missing.length}`,
            })
            await hydratePostUris(toDo.slice(off, off + HYDRATE_BATCH))
            await new Promise((r) => setTimeout(r, 400))
          }
          backlogRemaining += missing.length - toDo.length
        } catch {
          /* hydration is best-effort — unhydrated rows retry next run */
        }

        // One-time cleanup (v1.38.0): earlier versions wrote YAML records
        // for likes/reposts; those are now media-only. Remove the record
        // dirs the app itself created — media/ is NEVER touched.
        if (!(await kvGet<boolean>(`archive:yamlcleanup:${handle}`))) {
          try {
            const userRoot = await dir.getDirectoryHandle(handle)
            await userRoot.removeEntry('likes', { recursive: true }).catch(() => {})
            await userRoot.removeEntry('reposts', { recursive: true }).catch(() => {})
          } catch {
            /* user folder doesn't exist yet — nothing to clean */
          }
          await kvSet(`archive:yamlcleanup:${handle}`, true)
        }

        // ── the user's own posts/replies/reposts (AppView-hydrated) ──
        store.set({ phase: `@${handle} posts` })
        if (did) {
          const postCursor = (await kvGet<number>(cursorKey(handle, 'posts-v4'))) ?? 0
          let maxSeen = postCursor
          const rows = await db.posts.where('did').equals(did).toArray()
          for (const row of rows) {
            if (row.fetched_at <= postCursor) continue
            const p = await inflate<PostLike>(row)
            if (p?.uri) {
              const source = p.record?.reply ? 'reply' : 'post'
              if (await archivePost(dir, handle, source, p)) wrote++
            }
            maxSeen = Math.max(maxSeen, row.fetched_at)
            await new Promise((r) => setTimeout(r, PACE_MS))
          }
          if (maxSeen > postCursor) await kvSet(cursorKey(handle, 'posts-v4'), maxSeen)
        }

        // ── likes + reposts: archive the SUBJECT post's media only. Cursor
        // keys are v2 so pre-rule folders re-walk once under the new rules
        // (idempotent — existing files are simply overwritten). ──
        for (const src of [
          // Owner's order: reposts before likes (posts walked above).
          // v4 cursors: re-walk once more now that the archiver hydrates
          // missing subjects itself — likes/reposts skipped in earlier
          // passes (subject never viewed → never in db.posts) get their
          // media this time. Disk-first checks keep re-walks cheap.
          { collection: 'app.bsky.feed.repost', source: 'repost' as const, cursor: 'reposts-v4', label: 'reposts' },
          { collection: 'app.bsky.feed.like', source: 'like' as const, cursor: 'likes-v4', label: 'likes' },
        ]) {
          store.set({ phase: `@${handle} ${src.label}` })
          const cur = (await kvGet<number>(cursorKey(handle, src.cursor))) ?? 0
          let maxSeen = cur
          const rows = await db.records
            .where('[handle+collection+rkey]')
            .between([handle, src.collection, ''], [handle, src.collection, '￿'])
            .toArray()
          for (const row of rows) {
            if (row.fetched_at <= cur) continue
            const val = await inflate<{ subject?: { uri?: string } }>(row)
            const subjectUri = val?.subject?.uri
            if (subjectUri) {
              const postRow = await db.posts.get(subjectUri)
              const p = postRow ? await inflate<PostLike>(postRow) : null
              // mediaOnly: text-only (or uncached) subjects are skipped —
              // likes/reposts are archived for their images/video posters.
              if (p?.uri && (await archivePost(dir, handle, src.source, p, true))) {
                wrote++
              }
            }
            maxSeen = Math.max(maxSeen, row.fetched_at)
            await new Promise((r) => setTimeout(r, PACE_MS))
          }
          if (maxSeen > cur) await kvSet(cursorKey(handle, src.cursor), maxSeen)
        }

        if (wrote > 0) {
          const prev = (await kvGet<number>(countKey(handle))) ?? 0
          await kvSet(countKey(handle), prev + wrote)
          wroteTotal += wrote
        }
      }

      // ── index.yaml ──
      const counts: string[] = ['# bskyhq disk archive index', `updated: ${new Date().toISOString()}`, 'users:']
      let grand = 0
      for (const tab of useTabs.getState().tabs) {
        const n = (await kvGet<number>(countKey(tab.handle))) ?? 0
        grand += n
        counts.push(`  - handle: ${tab.handle}`, `    archived: ${n}`)
      }
      counts.push(
        '# videos are saved as .ts (MPEG-TS, H.264 as served by Bluesky).',
        '# to shrink them: ffmpeg -i in.ts -c:v libx265 -crf 26 -c:a copy out.mp4',
      )
      await writeFile(dir, ['index.yaml'], counts.join('\n') + '\n')
      useArchive.getState().set({ totalArchived: grand })

      // ── verified eviction: anything whose bytes are CONFIRMED on disk
      // no longer needs a Cache Storage copy. The SW serves mapped URLs
      // from the folder, so tiles keep rendering — including posts long
      // deleted upstream. Entries never verify → never evicted.
      store.set({ phase: 'evicting verified cache entries' })
      const evicted = await evictArchived(dir)
      useArchive.getState().set({ evicted })
    } catch (e) {
      useArchive.getState().set({ error: e instanceof Error ? e.message : String(e) })
    } finally {
      runningLocal = false
      useArchive.getState().set({ running: false, phase: null, lastRunAt: Date.now() })
      if (wroteTotal > 0) {
        // eslint-disable-next-line no-console
        console.info(`[bskyhq] archived ${wroteTotal} new item(s) to disk`)
      }
      // Self-chain: a backlog means a big history is still draining —
      // keep going (short breather) instead of waiting for the next
      // gallery refresh. Converges to zero, then goes quiet.
      if (backlogRemaining > 0) {
        // eslint-disable-next-line no-console
        console.info(`[bskyhq] archive backlog: ${backlogRemaining} items — continuing in 30s`)
        setTimeout(() => void runArchiver(), 30_000)
      }
    }
  }

  const locks = (navigator as Navigator & { locks?: LockManager }).locks
  if (locks?.request) {
    await locks.request('bskyhq-archiver', { ifAvailable: true }, async (lock) => {
      if (lock) await run()
    })
  } else {
    await run()
  }
}

/** Boot wiring. Primary trigger: the gallery-refreshed event (fires right
 *  after a poll lands fresh content), debounced and spaced so rapid polls
 *  don't stack runs. The 15-min interval is only a fallback for sessions
 *  where the gallery never refreshes. Background runs never prompt — if
 *  permission lapsed, Settings surfaces a one-click re-grant instead.
 *  Returns a cleanup fn. */
export function initArchiver(): () => void {
  // Scrub the v1.40.0 repo-fallback contamination FIRST — this fixes the
  // app's profile views even with no archive folder connected.
  void decontaminateRecords()
  // Surface connection status early so Settings shows the right state.
  const probe = setTimeout(() => void getDir(false), 3_000)
  const boot = setTimeout(() => void runArchiver(), BOOT_DELAY_MS)
  let lastEventRunAt = 0
  let debounce: ReturnType<typeof setTimeout> | null = null
  const onRefreshed = () => {
    if (debounce) clearTimeout(debounce)
    debounce = setTimeout(() => {
      if (Date.now() - lastEventRunAt < MIN_RUN_SPACING_MS) return
      lastEventRunAt = Date.now()
      void runArchiver()
    }, REFRESH_DEBOUNCE_MS)
  }
  window.addEventListener('bskyhq:gallery-refreshed', onRefreshed)
  // Fallback only: catches sessions where the gallery never polls (e.g.
  // the user parked on a profile page the whole time).
  const every = setInterval(() => void runArchiver(), PERIODIC_MS)
  return () => {
    clearTimeout(probe)
    clearTimeout(boot)
    if (debounce) clearTimeout(debounce)
    window.removeEventListener('bskyhq:gallery-refreshed', onRefreshed)
    clearInterval(every)
  }
}
